/**
 * Phase 6 cross-cutting PR 2 — `useNotifications` hook.
 *
 * **UX Session 2 F1 fix (2026-05-30): refactored to a Context Provider.**
 * Before this refactor, `useNotifications` was a plain hook with local
 * `useState` + its own fetch/poll effect — each call created an
 * INDEPENDENT instance. `/v2/today` renders up to three consumers
 * (`<BellTrigger>` in the SideNav footer AND the mobile TopBar, plus
 * `<TopRailUnread>` at Ultra), so it spun up three independent polls.
 * Worse, the mount effect depended on `fetchNotifications`, whose
 * identity tracked `prefs → athleteContext`; athlete-context mutates
 * several times during a load (the proactive/motivation writes), so
 * each settle re-fired the initial fetch. Net: ~6 `GET /api/notifications`
 * per page load (audit F1).
 *
 * The Provider pattern fixes both: one instance lives at the top of
 * the app (single fetch + single 60s poll); all `useNotifications()`
 * consumers read the SAME state via React Context. And the fetch now
 * pulls RAW rows once — allowlist + per-type preference filtering
 * happens at derive time (`useMemo`), so a preference/context change
 * re-derives the view WITHOUT triggering a refetch. Mirrors the
 * `AthleteContextProvider` refactor (which fixed the identical
 * per-call-instance anti-pattern).
 *
 * **Locked 5-key allowlist** — the derived view FILTERS server
 * responses to only surface notifications whose `type` is in
 * `NOTIFICATION_ALLOWLIST`. Defense-in-depth: even if the server emits
 * a non-allowlisted type (e.g., a stale vanilla-bell-era row), the
 * React UI never shows it.
 *
 * **Preference filter** — additionally honors
 * `_notification_prefs.<type> === false` from athlete context. Rows
 * whose type is allowlisted but pref-disabled are filtered out. When
 * the pref map is empty, default is show-all.
 *
 * **Visibility-aware** — pauses polling when the tab is hidden
 * (`document.visibilityState === 'hidden'`) and resumes immediately
 * when the tab regains focus. Saves a server tick every minute of
 * background time.
 *
 * **Optimistic mark-read** — clears the read state client-side BEFORE
 * the POST resolves. On failure, the optimistic change rolls back (the
 * next poll restores server truth either way).
 *
 * Sourced from
 *   - vanilla `src/renderer/js/notifications.js` (port, with React idioms)
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §1
 *   - `.claude/references/phase-6-cross-cutting-design/HANDOFF.md` PR 2
 */

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuthFetch } from './useAuthFetch';
import { useAuthContext } from '../contexts/AuthContext';
import { useAthleteContext } from './useAthleteContext';
import {
  NOTIFICATION_ALLOWLIST,
  isAllowlistedNotificationType,
  type NotificationType,
} from '../lib/notifAllowlist';

const POLL_INTERVAL_MS = 60_000;

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string | null;
  is_read: boolean;
  created_at: string;
  /**
   * NU-1 — untrusted server-stored payload. Only consulted for
   * `plan_nudge` rows (deep-link `href`, validated through
   * `sanitizeInternalHref` in NotifRow). Optional: rows predating the
   * column-read and all mocked rows omit it.
   */
  metadata?: Record<string, unknown> | null;
}

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  /** Force a refetch outside the 60s tick (e.g., after a mark-all click). */
  refetch: () => Promise<void>;
  /** Mark a single notification read (optimistic; rolls back on failure). */
  markAsRead: (id: string) => Promise<void>;
  /** Mark every notification read (optimistic; rolls back on failure). */
  markAllAsRead: () => Promise<void>;
}

const EMPTY_RESULT: UseNotificationsResult = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  refetch: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
};

const NotificationsContext = createContext<UseNotificationsResult>(EMPTY_RESULT);

// ─────────────────────────────────────────────────────────────
// Provider (single source of truth — one fetch + one poll)
// ─────────────────────────────────────────────────────────────

export interface NotificationsProviderProps {
  children: ReactNode;
}

/**
 * Wraps the app — must mount inside `<AuthProvider>` and
 * `<AthleteContextProvider>` (depends on `user` + the athlete-context
 * preference map). Single instance per app; every `useNotifications()`
 * consumer shares this state, so there is exactly ONE poll regardless
 * of how many bells / chips are on screen.
 */
export function NotificationsProvider({ children }: NotificationsProviderProps): JSX.Element {
  const fetchAuthed = useAuthFetch();
  const { user } = useAuthContext();
  const userId = user?.id ?? null;
  const { raw: athleteContext } = useAthleteContext();

  // RAW server rows (pre-filter). Allowlist + preference filtering is
  // applied at derive time below — NOT here — so a preference/context
  // change re-derives the view without forcing a refetch.
  const [rawRows, setRawRows] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Ref mirror for optimistic-mutation rollback (capture-before-mutate
  // without taking `rawRows` as a callback dependency).
  const rawRowsRef = useRef(rawRows);
  rawRowsRef.current = rawRows;

  // F3 (2026-09-21) — ref mirror of the latest signed-in uid, updated every
  // render (not inside an effect, so it's current before any awaited fetch
  // below can resolve). An in-place account switch (uid A → uid B, no
  // sign-out) doesn't cancel a fetch already in flight for A; without this
  // guard A's late response lands under B once it resolves.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Stable fetch — depends only on `[fetchAuthed, user]` (both stable:
  // `fetchAuthed` is memoized on the Supabase client, `user` only
  // changes on sign-in/out). Crucially it does NOT depend on the
  // preference filter, so athlete-context settling can't re-fire it.
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    // F3 — captured before the await; compared against the ref (the
    // latest uid) after it resolves so a response for an
    // since-switched-away account is dropped rather than committed.
    const requestUserId = userId;
    try {
      const res = await fetchAuthed('/api/notifications');
      if (!res.ok) throw new Error(`GET /api/notifications failed (${res.status})`);
      const data = (await res.json()) as { notifications?: Notification[] };
      if (userIdRef.current !== requestUserId) return;
      setRawRows(data.notifications ?? []);
      setError(null);
    } catch (err) {
      if (userIdRef.current !== requestUserId) return;
      setError(err instanceof Error ? err : new Error('Unknown notifications fetch error'));
    } finally {
      if (userIdRef.current === requestUserId) setIsLoading(false);
    }
  }, [fetchAuthed, user, userId]);

  // Latest fetch fn via ref so the poll effect can depend ONLY on the
  // primitive `userId`, never on the fetch fn's identity. Auth / Supabase
  // object identity settles once or twice right after sign-in resolves
  // (user object re-created, INITIAL_SESSION → SIGNED_IN, token refresh); if
  // the effect keyed on `fetchNotifications` (which closes over `user`), each
  // settle would tear down + re-run it, firing an extra load fetch — observed
  // live as a 2× `GET /api/notifications` clustered at load. Keying on the
  // stable `userId` string fires exactly ONE fetch per signed-in user.
  const fetchRef = useRef(fetchNotifications);
  fetchRef.current = fetchNotifications;

  // Poll setup — 60s interval, visibility-aware. Pauses on tab blur,
  // resumes (and immediately ticks once) on tab focus. Keyed on `userId`
  // only, so it runs exactly once per signed-in session — not per render.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // F6 (2026-09-21) — useLayoutEffect, not useEffect: the reset below must
  // land in the SAME committed frame the uid changes in, or one paint can
  // show account A's rows/unread count under account B's uid before this
  // fires.
  useLayoutEffect(() => {
    // B159 — an in-place switch (uid A → uid B, no sign-out) must not go on
    // showing account A's rows/unread count while account B's fetch is in
    // flight, or indefinitely if that fetch hangs. Clear on ANY `userId`
    // change (not only to null) before the new fetch is fired below.
    setRawRows([]);
    setError(null);

    if (!userId) {
      // Signed-out: clear any existing poll + reset state.
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsLoading(false);
      return;
    }

    // First tick + interval.
    setIsLoading(true);
    void fetchRef.current();
    intervalRef.current = setInterval(() => void fetchRef.current(), POLL_INTERVAL_MS);

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else {
        // Returning to foreground: refetch immediately + restart interval.
        void fetchRef.current();
        intervalRef.current = setInterval(() => void fetchRef.current(), POLL_INTERVAL_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [userId]);

  // Mark single row read — optimistic. Mutates the raw row; the derived
  // view recomputes `unreadCount`. Rolls back to the pre-mutation rows
  // on failure (the next 60s tick re-establishes truth either way).
  const markAsRead = useCallback(
    async (id: string) => {
      const prevRows = rawRowsRef.current;
      setRawRows((rows) => rows.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      try {
        const res = await fetchAuthed('/api/notifications/read', {
          method: 'POST',
          body: JSON.stringify({ notificationId: id }),
        });
        if (!res.ok) throw new Error(`POST /api/notifications/read failed (${res.status})`);
      } catch (err) {
        setRawRows(prevRows);
        setError(err instanceof Error ? err : new Error('Mark-as-read failed'));
      }
    },
    [fetchAuthed],
  );

  // Mark-all-read — optimistic. Clears the badge immediately.
  const markAllAsRead = useCallback(async () => {
    const prevRows = rawRowsRef.current;
    setRawRows((rows) => rows.map((n) => ({ ...n, is_read: true })));
    try {
      const res = await fetchAuthed('/api/notifications/read', {
        method: 'POST',
        body: JSON.stringify({ markAll: true }),
      });
      if (!res.ok) throw new Error(`POST /api/notifications/read (markAll) failed (${res.status})`);
    } catch (err) {
      setRawRows(prevRows);
      setError(err instanceof Error ? err : new Error('Mark-all-read failed'));
    }
  }, [fetchAuthed]);

  // Preference map — `_notification_prefs.<type> === false` hides that
  // type. Empty map = show-all default.
  const prefs = useMemo<Record<string, boolean>>(() => {
    const raw = (athleteContext as Record<string, unknown> | null)?._notification_prefs;
    return (raw && typeof raw === 'object' ? raw : {}) as Record<string, boolean>;
  }, [athleteContext]);

  // Derived view — allowlist + preference filter applied here (not at
  // fetch time), so prefs/context changes re-derive without a refetch.
  const value = useMemo<UseNotificationsResult>(() => {
    const filtered = rawRows.filter((n) => {
      if (!isAllowlistedNotificationType(n.type)) return false;
      if (prefs[n.type] === false) return false;
      return true;
    });
    const unreadCount = filtered.filter((n) => !n.is_read).length;
    return {
      notifications: filtered,
      unreadCount,
      isLoading,
      error,
      refetch: fetchNotifications,
      markAsRead,
      markAllAsRead,
    };
  }, [rawRows, prefs, isLoading, error, fetchNotifications, markAsRead, markAllAsRead]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

// ─────────────────────────────────────────────────────────────
// Consumer hook (unchanged signature)
// ─────────────────────────────────────────────────────────────

/**
 * Returns the shared notifications state. Same signature as the
 * pre-F1 hook so existing consumers (BellTrigger, TopRailUnread) keep
 * working without changes.
 *
 * Outside `<NotificationsProvider>` (e.g., isolated tests), returns the
 * no-op `EMPTY_RESULT` so consumers don't crash. Production always
 * renders inside the Provider via `main.tsx`.
 */
export function useNotifications(): UseNotificationsResult {
  return useContext(NotificationsContext);
}

// Re-export the allowlist + type for consumers that don't want to
// import from `lib/notifAllowlist` directly.
export { NOTIFICATION_ALLOWLIST };
export type { NotificationType };
